import { useTranslation } from 'react-i18next'
import {
  Button,
  FileDownloadIcon,
  LoadingDots,
  Span,
  Text,
} from '@/components/atoms'
import { FileDocumentIcon } from '@/components/atoms/icons/file'
import { Modal } from '../modal'

export type DocumentPreviewKind = 'image' | 'pdf' | 'unsupported'

interface DocumentPreviewProps {
  isOpen: boolean
  fileName?: string
  contentType?: string
  kind: DocumentPreviewKind
  /** Local object URL of the file, shown in an iframe (PDF) or img */
  objectUrl?: string
  isLoading: boolean
  hasError: boolean
  canDownload: boolean
  onClose: () => void
  onDownload: () => void
  /** The browser couldn't render the image (e.g. HEIC outside Safari) */
  onRenderError: () => void
}

export function DocumentPreview({
  isOpen,
  fileName,
  contentType,
  kind,
  objectUrl,
  isLoading,
  hasError,
  canDownload,
  onClose,
  onDownload,
  onRenderError,
}: DocumentPreviewProps) {
  const { t } = useTranslation()

  return (
    <Modal isOpen={isOpen} onClose={onClose} className="md:max-w-4xl">
      <div className="flex flex-col gap-4 p-6 pt-8">
        <div className="flex flex-col gap-1 pr-10">
          <Text className="truncate font-bold text-neutral-900">
            {fileName}
          </Text>
          <Span className="text-neutral-600">{contentType}</Span>
        </div>

        <div className="flex h-[60dvh] items-center justify-center overflow-hidden rounded-2xl border border-neutral-100 bg-(--file-upload-bg)">
          {isLoading ? (
            <LoadingDots className="loading-xl" />
          ) : hasError ? (
            <Text className="text-error-base px-6 text-center">
              {t('claims.detail.previewError')}
            </Text>
          ) : kind === 'pdf' ? (
            <iframe src={objectUrl} title={fileName} className="size-full" />
          ) : kind === 'image' ? (
            <img
              src={objectUrl}
              alt={fileName}
              onError={onRenderError}
              className="size-full object-contain"
            />
          ) : (
            <div className="flex flex-col items-center gap-2 px-6 text-center">
              <FileDocumentIcon className="size-10 text-neutral-300" />
              <Text className="text-neutral-600">
                {t('claims.detail.previewUnsupported')}
              </Text>
            </div>
          )}
        </div>

        <div className="flex justify-end">
          <Button
            variant="secondary"
            onClick={onDownload}
            disabled={!canDownload}
            data-testid="document-preview-download"
          >
            <FileDownloadIcon className="size-5" />
            {t('claims.detail.download')}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
